import React, { useRef, useEffect, useCallback, forwardRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';

interface ScrollableContainerProps {
    onBeginningReached: () => void;
    threshold?: number;
    children: React.ReactNode;
    loading?: boolean;
}

const ScrollableContainer = forwardRef<HTMLDivElement, ScrollableContainerProps>(
    ({ onBeginningReached, threshold = 100, children, loading }, ref) => {
        const internalRef = useRef<HTMLDivElement | null>(null);
        const scrollRef = (ref as React.MutableRefObject<HTMLDivElement | null>) || internalRef;
        const { themeStyle } = useThemeContext();

        const handleScroll = useCallback(() => {
            const container = scrollRef.current;
            if (container && container.scrollTop <= threshold) {
                onBeginningReached();
            }
        }, [onBeginningReached, threshold, scrollRef]);

        useEffect(() => {
            const container = scrollRef.current;
            if (container) {
                container.addEventListener('scroll', handleScroll);
            }
            return () => {
                if (container) {
                    container.removeEventListener('scroll', handleScroll);
                }
            };
        }, [handleScroll, scrollRef]);

        return (
            <Box
                ref={scrollRef}
                sx={{
                    overflowY: 'auto',
                    height: '100%',
                    position: 'relative',
                }}>
                {/* Loading Indicator */}
                {loading && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            p: 2,
                        }}>
                        <CircularProgress size={24} sx={{ color: theme[themeStyle].tag_white }} />
                    </Box>
                )}
                {children}
            </Box>
        );
    },
);

export { ScrollableContainer };
